import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, FilterOperator, OrderInput } from '#features/api/types';
import { ApiError } from '#features/api/utils/ApiError';
import { PostDeleteReason } from '#features/deletion/types';
import { notePushWorthyAction } from '#features/notifications/utils/pushPrompt';
import { isDefined } from '#utils/isDefined';
import { PostInput } from '../types';
import { postFragment, postOgFragment } from './fragments';

const getPostByIdDoc = graphql(
  `
    query Post($id: UUID!) {
      post {
        byId(id: $id) {
          ...PostFragment
        }
      }
    }
  `,
  [postFragment]
);

export const getPostById = (input: { id: string }) =>
  gqlFetcher(getPostByIdDoc, input).then((data) => data.post.byId);

/**
 * Lean query for the OG-image generator. Uses a named fragment spread
 * (PostOgFragment) so the backend sees it as a FragmentDefinition, not an
 * InlineFragment (RequestContextInjector ClassCasts the latter to Field).
 * The fragment skips auth-gated resolvers (discordVoiceChannel etc.) so
 * the anonymous SSR OG path doesn't take the whole response to 401.
 */
const getPostForOgDoc = graphql(
  `
    query PostForOg($id: UUID!) {
      post {
        byId(id: $id) {
          ...PostOgFragment
        }
      }
    }
  `,
  [postOgFragment]
);

export const getPostForOg = (input: { id: string }) =>
  gqlFetcher(getPostForOgDoc, input).then((data) => data.post.byId);

export const getPostByIdQueryOptions = (input: { id: string }) =>
  queryOptions({
    queryKey: ['post', input.id],
    queryFn: () => getPostById(input),
    retry: (failureCount, error) => {
      if (error instanceof ApiError) {
        return error.status === 0 && failureCount < 2;
      }

      if (error instanceof Error && error.name === 'TypeError') {
        return failureCount < 2;
      }

      return false;
    },
    retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 2000),
  });

const getCanCreatePostDoc = graphql(`
  query CanCreatePost($gameId: UUID!) {
    post {
      myPostByGameId(gameId: $gameId) {
        id
      }
    }
  }
`);

export const getCanCreatePost = (input: { gameId: string }) =>
  gqlFetcher(getCanCreatePostDoc, input).then((data) => data.post.myPostByGameId);

export const getCanCreatePostQueryOptions = (input: { gameId: string }) =>
  queryOptions({
    queryKey: ['can-create-post', input.gameId],
    queryFn: () => getCanCreatePost(input),
  });

const getMyPostGameIdsDoc = graphql(`
  query MyPostGameIds {
    post {
      myGameIds
    }
  }
`);

export const getMyPostGameIds = () =>
  gqlFetcher(getMyPostGameIdsDoc, {})
    .then((data) => new Set(data.post.myGameIds?.filter(isDefined) ?? []))
    // Graceful fallback if backend doesn't have myGameIds yet (deploy ordering).
    // Returns empty set → tags hidden; backend still enforces duplicate post rule on create.
    .catch(() => new Set<string>());

export const getMyPostGameIdsQueryOptions = () =>
  queryOptions({
    queryKey: ['my-post-game-ids'],
    queryFn: getMyPostGameIds,
    staleTime: 60_000,
  });

// `preview` returns the PostResult union and the OG handler narrows on
// __typename, which only arrives if selected here: PostFragment doesn't
// include it and gql.tada adds nothing at runtime. A plain __typename field
// is safe — an `... on Post` inline fragment is not (see postOgFragment).
const getPostPreviewDoc = graphql(
  `
    query PostPreview($input: PostInput!, $userId: UUID!) {
      post {
        preview(input: $input, userId: $userId) {
          __typename
          ...PostFragment
        }
      }
    }
  `,
  [postFragment]
);

export const getPostPreview = (input: { input: PostInput; userId: string }) =>
  gqlFetcher(getPostPreviewDoc, input).then((data) => data.post.preview);

const getPostsDoc = graphql(
  `
    query Posts(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
      $onlineOnly: Boolean
    ) {
      post {
        list(
          offset: $offset
          limit: $limit
          orders: $orders
          filters: $filters
          onlineOnly: $onlineOnly
        ) {
          posts {
            ...PostFragment
          }
          hasMore
          totalCount
          presenceUnavailable
        }
      }
    }
  `,
  [postFragment]
);

export type GetPostsInput = {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
  /**
   * «Только те, кто сейчас в сети». Считает сервер: он один знает и живые сокеты, и всю выдачу
   * целиком. Клиент отфильтровать это не может — на руках у него всегда только текущая страница.
   */
  onlineOnly?: boolean;
};

const createPlainPostFilter = (): FilterInput => ({
  field: 'session',
  operator: FilterOperator.EQUALS,
  value: false,
  filters: [],
});

export const withPlainPostFilters = (filters: FilterInput[] = []) => [
  createPlainPostFilter(),
  ...filters,
];

export const getPosts = (input: GetPostsInput) =>
  gqlFetcher(getPostsDoc, input).then((data) => ({
    records: data.post.list?.posts?.filter(isDefined),
    hasMore: data.post.list?.hasMore,
    totalCount: data.post.list?.totalCount,
    // Сервис присутствия не ответил, и выдача приехала нефильтрованной. Молчать нельзя: включённый
    // чип над списком, где половина людей офлайн, — это то же враньё, от которого мы уходим.
    presenceUnavailable: data.post.list?.presenceUnavailable ?? false,
  }));

export const getPlainPosts = (input: GetPostsInput) =>
  getPosts({
    ...input,
    filters: withPlainPostFilters(input.filters),
  });

const getUniqueUserPostsDoc = graphql(
  `
    query UniqueUserPosts(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
      $uniqueByUser: Boolean
    ) {
      post {
        list(
          offset: $offset
          limit: $limit
          orders: $orders
          filters: $filters
          uniqueByUser: $uniqueByUser
        ) {
          posts {
            ...PostFragment
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export type GetUniqueUserPostsInput = GetPostsInput & { uniqueByUser?: boolean };

export const getUniqueUserPosts = (input: GetUniqueUserPostsInput) =>
  gqlFetcher(getUniqueUserPostsDoc, input).then((data) => ({
    records: data.post.list?.posts?.filter(isDefined),
    hasMore: data.post.list?.hasMore,
    totalCount: data.post.list?.totalCount,
  }));

const getPostsCountDoc = graphql(`
  query PostsCount($filters: [FilterInput]) {
    post {
      list(offset: 0, limit: 0, filters: $filters) {
        totalCount
      }
    }
  }
`);

export const getPostsCount = (filters: FilterInput[]) =>
  gqlFetcher(getPostsCountDoc, { filters }).then((data) => data.post.list?.totalCount ?? 0);

/**
 * Счётчик для кнопки «Показать N» в мобильной шторке фильтров. limit: 0 — тело списка
 * не приезжает, только totalCount, поэтому запрос дешёвый и его не жалко дёргать
 * на каждое изменение фильтра, пока шторка открыта.
 */
export const getPostsCountQueryOptions = (filters: FilterInput[]) =>
  queryOptions({
    queryKey: ['posts-count', filters],
    queryFn: () => getPostsCount(filters),
    staleTime: 30_000,
  });

const getPostCountsByGamesDoc = graphql(`
  query PostCountsByGames($gameIds: [UUID!]!) {
    post {
      postCountsByGames(gameIds: $gameIds) {
        gameId
        count
      }
    }
  }
`);

export const getGamePostCountsQueryOptions = (gameIds: string[]) =>
  queryOptions({
    queryKey: ['game-post-counts', gameIds],
    queryFn: async () => {
      const data = await gqlFetcher(getPostCountsByGamesDoc, { gameIds });
      const counts = data.post.postCountsByGames ?? [];
      return Object.fromEntries(counts.map((item) => [item.gameId, item.count])) as Record<
        string,
        number
      >;
    },
    staleTime: 60_000,
  });

const getFavouritePostsDoc = graphql(
  `
    query FavouritePosts(
      $offset: Int
      $limit: Int
      $orders: [OrderInput]
      $filters: [FilterInput]
    ) {
      favourite {
        list(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
          favourites {
            data {
              ...PostFragment
            }
          }
          hasMore
          totalCount
        }
      }
    }
  `,
  [postFragment]
);

export const getFavouritePosts = (input: GetPostsInput) =>
  gqlFetcher(getFavouritePostsDoc, input).then((data) => ({
    records: data.favourite.list?.favourites?.filter(isDefined),
    hasMore: data.favourite.list?.hasMore,
    totalCount: data.favourite.list?.totalCount,
  }));

export const getFavouritePlainPosts = (input: GetPostsInput) =>
  getFavouritePosts({
    ...input,
    filters: withPlainPostFilters(input.filters),
  });

const createPostDoc = graphql(`
  mutation CreatePost($input: PostInput!) {
    post {
      create(input: $input) {
        __typename
        ... on Post {
          id
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
      }
    }
  }
`);

export const createPost = (input: PostInput) =>
  gqlFetcher(createPostDoc, { input }).then((data) => {
    // Заявка опубликована — человек ждёт откликов, и это повод спросить про уведомления.
    notePushWorthyAction();
    return data.post.create;
  });

const editPostDoc = graphql(`
  mutation EditPost($input: PostInput!) {
    post {
      update(input: $input) {
        __typename
        ... on GlobalError {
          errorCode
          errorMessage
        }
        ... on InputError {
          errors {
            messagesErrors
            name
          }
        }
        ... on Post {
          id
        }
      }
    }
  }
`);

export const editPost = (input: PostInput) =>
  gqlFetcher(editPostDoc, { input }).then((data) => data.post.update);

const bumpPostDoc = graphql(`
  mutation BumpPost($id: UUID!) {
    post {
      bump(id: $id) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const bumpPost = (input: { id: string }) =>
  gqlFetcher(bumpPostDoc, input).then((data) => data.post.bump);

const deletePostDoc = graphql(`
  mutation DeletePost($id: UUID!, $reason: PostDeleteReason, $reasonText: String) {
    post {
      delete(id: $id, reason: $reason, reasonText: $reasonText) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export const deletePost = (input: { id: string; reason?: PostDeleteReason; reasonText?: string }) =>
  gqlFetcher(deletePostDoc, input).then((data) => data.post.delete);
